import type { GetCurrentMemberFn } from '@/features/iam'
import { type Collaborator } from '@/features/jobs/Job'
import { computed } from 'mobx'

/**
 * A collaborator on a job, as the collaborators card shows it.
 */
export class CollaboratorViewModel {
  constructor(
    public readonly collaborator: Collaborator,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {}

  /**
   * The collaborator's member.
   */
  @computed
  get member() {
    return this.collaborator.member
  }

  /**
   * Whether the collaborator is a Pro or a Client.
   */
  @computed
  get type() {
    return this.collaborator.type
  }

  /**
   * The collaborator's type, as the card shows it.
   */
  @computed
  get formattedType() {
    return this.collaborator.type === 'Pro' ? 'Pro' : 'Client'
  }

  /**
   * The collaborator's role on the job.
   */
  @computed
  get role() {
    return this.collaborator.role
  }

  /**
   * The collaborator's role, as the card shows it.
   */
  @computed
  get formattedRole() {
    return this.collaborator.role === 'Champion' ? 'Champion' : 'Supporting'
  }

  /**
   * The team the collaborator represents, if any.
   */
  @computed
  get team() {
    // Make sure we can actually find the owner team on the job.
    if (this.type === 'Client' && this.role === 'Champion') {
      return this.collaborator.job.ownerTeam?.id
        ? this.collaborator.job.teams.find(
            (x) => x.id === this.collaborator.job.ownerTeam?.id,
          )
        : null
    }
    return this.collaborator.team
  }

  /**
   * Whether the collaborator is the current user.
   */
  @computed
  get isCurrentUser() {
    return this.collaborator.member.isCurrentUser
  }

  /**
   * Whether the actor can view any of the collaborator's details.
   */
  @computed
  get canViewCollaboratorInfo() {
    return Boolean(this.canViewProInfo || this.canViewClientInfo || this.team)
  }

  /**
   * Whether the actor can view the collaborator's email.
   */
  @computed
  get canViewCollaboratorEmail() {
    return Boolean(
      (this.canViewProInfo || this.canViewClientInfo) && this.member.email,
    )
  }

  /**
   * Whether the actor can view the collaborator's phone number.
   */
  @computed
  get canViewCollaboratorPhoneNumber() {
    return Boolean(
      (this.canViewProInfo || this.canViewClientInfo) && this.member.phone,
    )
  }

  /**
   * Whether the actor can view the details of a Client collaborator.
   */
  @computed
  get canViewClientInfo() {
    return (
      this._getCurrentMember(this.collaborator.job.workspace.id).permissions
        .viewClientDetails && this.collaborator.type === 'Client'
    )
  }

  /**
   * Whether the actor can view Pro personal information.
   */
  @computed
  get canViewProInfo() {
    return this._getCurrentMember(this.collaborator.job.workspace.id)
      .permissions.adminProPiView
  }

  /**
   * Indicates whether the actor can view this collaborator in HQ.
   */
  @computed
  get canViewUserDetails() {
    return this._getCurrentMember(this.collaborator.job.workspace.id)
      .permissions.adminAccess
  }
}
